import React, { useEffect, useRef, useState } from 'react';
import { AlertBanner, Button, Tabs } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBell, faCompress, faExpand, faRocketLaunch, faServer } from '@fortawesome/pro-light-svg-icons';
import { Link, useParams } from 'react-router-dom';
import { FlatCard } from '@/components/FlatCard';
import { useLocalStorage } from '@/utils/hooks';
import {
    AwsHealth,
    AwsHealthSection,
    describeError,
    getAwsHealth,
    getSentryIssues,
    Health,
    HealthEventSource,
    SentryIssue,
    Stage,
    stages,
} from '@/services/systemHealth';
import { Loaded, SourceBadge } from './SourceBadge';
import { useSource } from './devActivity';
import ActivityTab from './ActivityTab';
import CustomersTab from './CustomersTab';
import StatisticsTab from './StatisticsTab';
import { Stat } from './Stat';
import ServiceReport from './ServiceReport';
import { formatCount, formatMs, formatPercent, orDash, timeAgo } from './format';
import theme from './SystemHealthPage.module.scss';

/** The backend refreshes AWS data once a minute (EB rate limits); polling at half that keeps the page within 30s of it. */
const pageTabs = [
    { id: 'health', label: 'Health' },
    { id: 'activity', label: 'Activity' },
    { id: 'customers', label: 'Customers' },
    { id: 'statistics', label: 'Statistics' },
];

const AWS_POLL_MS = 30_000;
const SENTRY_POLL_MS = 60_000;
const loadSentry = () => getSentryIssues().then((r) => ({ data: r.issues, error: r.error }));

const sourceIcon: Record<HealthEventSource, IconDefinition> = {
    deploy: faRocketLaunch,
    eb: faServer,
};

const feedFilters: { key: HealthEventSource | 'all'; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'deploy', label: 'Deploys' },
    { key: 'eb', label: 'Beanstalk' },
];

const stageLabel: Record<Stage, string> = {
    test: 'Test',
    uat: 'UAT',
    prod: 'Prod',
};

const healthLabel: Record<Health, string> = {
    ok: 'Healthy',
    degraded: 'Degraded',
    down: 'Down',
    unknown: 'No data',
};

/** Re-renders every second so clocks and "x ago" labels stay current. */
function useNow() {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    return now;
}

/**
 * Polls the stage's AWS health. Keeps the last snapshot (possibly of another stage) until the next one
 * arrives, so the stage tabs and the prod pill don't blank out on every switch or failed refresh.
 */
function useAwsHealth(stage: Stage) {
    const [latest, setLatest] = useState<AwsHealth | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        const load = () =>
            getAwsHealth(stage).then(
                (x) => {
                    if (!cancelled) {
                        setLatest(x);
                        setError(null);
                    }
                },
                (e) => {
                    if (!cancelled) {
                        console.error('[SystemHealth] loading AWS health failed', e);
                        setError(describeError(e));
                    }
                }
            );
        load();
        const id = setInterval(load, AWS_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [stage]);

    return { latest, error };
}

const SystemHealthPage: React.FC = () => {
    const pageRef = useRef<HTMLDivElement>(null);
    const { tab = 'health' } = useParams<{ tab?: string }>();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [feedFilter, setFeedFilter] = useState<HealthEventSource | 'all'>('all');
    const [stage, setStage] = useLocalStorage<Stage>('systemHealthEnv', 'prod');
    const { latest, error: awsError } = useAwsHealth(stage);
    const [sentry, reloadSentry] = useSource(loadSentry, SENTRY_POLL_MS);
    const [report, setReport] = useState<string | null>(null);
    const now = useNow();

    useEffect(() => {
        const onChange = () => setIsFullscreen(document.fullscreenElement === pageRef.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const sentryTotal = sentry.data?.reduce((sum, i) => sum + i.count, 0);

    const toggleFullscreen = () =>
        document.fullscreenElement ? document.exitFullscreen() : pageRef.current?.requestFullscreen();

    // Stage-specific panels only show data for the open tab; tabs and the pill use whatever arrived last.
    const data = latest?.stage === stage ? latest : null;
    const loading = !data && !awsError;
    const errorFor = (section: AwsHealthSection) => data?.errors.find((e) => e.section === section)?.message;
    const summaryFor = (s: Stage) => latest?.stages.find((x) => x.stage === s);

    // Prod drives the overall pill and the ticker colour, whichever tab is open; the tabs show the rest.
    const prod = summaryFor('prod');
    const listed = latest && !latest.errors.some((e) => e.section === 'environments');
    const overall: Health = listed && prod ? prod.health : 'unknown';
    const overallText = !latest
        ? awsError
            ? 'Could not load AWS health'
            : 'Loading…'
        : !listed || !prod
          ? 'Could not list environments'
          : prod.unhealthy.length === 0
            ? `Prod: all ${prod.total} environments healthy`
            : `Prod: ${prod.unhealthy.length} of ${prod.total} environments need attention`;

    const services = data?.services ?? [];
    const metrics = data?.metrics ?? null;
    const alarms = data?.alarms ?? null;

    // Test has a couple of applications with two environments; tell those apart by environment name.
    const names = services.map((s) => s.name);
    const duplicateNames = new Set(names.filter((n, i) => names.indexOf(n) !== i));

    return (
        <div ref={pageRef} className={theme.page}>
            <Ticker items={buildTldr(latest, awsError, sentry.data, now)} tone={overall} />
            <header className={theme.header}>
                <div className={theme.titleBlock}>
                    <h2 className={theme.title}>System health</h2>
                    <Tabs activeId={stage} onChange={(id) => setStage(id as Stage)} variant="pill" size="sm">
                        {stages.map((s) => {
                            const summary = summaryFor(s);
                            return (
                                <Tabs.Tab
                                    key={s}
                                    id={s}
                                    label={
                                        <span className={theme.envTab}>
                                            <span className={`${theme.dot} ${theme[summary?.health ?? 'unknown']}`} />
                                            {stageLabel[s]}
                                        </span>
                                    }
                                    badge={summary?.unhealthy.length || undefined}
                                />
                            );
                        })}
                    </Tabs>
                </div>
                <div className={`${theme.overall} ${theme[overall]}`}>
                    <span className={`${theme.dot} ${theme[overall]} ${theme.pulse}`} />
                    {overallText}
                    {latest && awsError && <span className={theme.stale}>(stale: {awsError})</span>}
                </div>
                <div className={theme.headerRight}>
                    <span className={theme.clock}>{new Date(now).toLocaleTimeString('en-GB')}</span>
                    <Button
                        size="sm"
                        variant="tertiary"
                        icon={isFullscreen ? faCompress : faExpand}
                        onClick={toggleFullscreen}>
                        {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    </Button>
                </div>
            </header>

            <Tabs activeId={tab} variant="underline" className={theme.pageTabs}>
                {pageTabs.map((t) => (
                    <Tabs.Tab key={t.id} id={t.id} as={Link} to={`/system-health/${t.id}`} label={t.label} />
                ))}
            </Tabs>

            {tab === 'activity' ? (
                <ActivityTab now={now} />
            ) : tab === 'customers' ? (
                <CustomersTab now={now} />
            ) : tab === 'statistics' ? (
                <StatisticsTab now={now} />
            ) : (
                <>
                    <section className={theme.stats}>
                        <Stat label="Requests / min" history={metrics?.requestsPerMinute} format={formatCount} />
                        <Stat label="5xx rate" history={metrics?.errorRate} format={formatPercent} isBad={(v) => v > 1} />
                        <Stat label="p95 latency" history={metrics?.p95Ms} format={formatMs} isBad={(v) => v > 500} />
                        <div className={theme.stat}>
                            <div className={theme.statLabel}>
                                Sentry events (24h) {!sentry.data && <SourceBadge source={sentry} />}
                            </div>
                            <div className={theme.statValue}>{sentryTotal == null ? '—' : formatCount(sentryTotal)}</div>
                            <div className={theme.muted}>
                                {sentry.data ? `across ${sentry.data.length} unresolved issues` : 'waiting for Sentry'}
                            </div>
                        </div>
                        {metrics?.dbCpu && (
                            <Stat
                                label="DB CPU"
                                history={metrics.dbCpu}
                                format={(v) => `${Math.round(v)}%`}
                                isBad={(v) => v > 80}
                            />
                        )}
                        <div className={theme.stat}>
                            <div className={theme.statLabel}>Active alarms</div>
                            <div
                                className={`${theme.statValue} ${alarms?.length ? theme.warning : alarms ? theme.success : ''}`}>
                                {alarms ? alarms.length : '—'}
                            </div>
                            <div className={theme.muted}>
                                {!alarms ? 'unavailable' : alarms.length ? 'see CloudWatch alarms below' : 'none firing'}
                            </div>
                        </div>
                    </section>
                    <SectionError message={awsError ?? undefined} prefix="AWS health unavailable" />
                    <SectionError message={errorFor('metrics')} prefix="Metrics unavailable" />

                    <div className={theme.main}>
                        <FlatCard title={`Services · ${stageLabel[stage]}`} className={theme.servicesCard} loading={loading}>
                            <SectionError message={errorFor('environments')} prefix="Environments unavailable" />
                            <SectionError message={errorFor('health')} prefix="Instance health unavailable" />
                            <div className={theme.serviceGrid}>
                                {services.map((s) => (
                                    <div
                                        key={s.environmentName}
                                        className={`${theme.service} ${theme.serviceClickable} ${theme[s.health]}`}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Open report for ${s.environmentName}`}
                                        onClick={() => setReport(s.environmentName)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setReport(s.environmentName);
                                            }
                                        }}>
                                        <div className={theme.serviceName}>
                                            <span
                                                className={`${theme.dot} ${theme[s.health]}`}
                                                title={s.healthStatus ?? healthLabel[s.health]}
                                            />
                                            {s.name}
                                            {s.status && s.status !== 'Ready' && (
                                                <span className={theme.statusTag}>{s.status}</span>
                                            )}
                                        </div>
                                        {duplicateNames.has(s.name) && (
                                            <div className={`${theme.muted} ${theme.environmentName}`}>
                                                {s.environmentName}
                                            </div>
                                        )}
                                        <dl className={theme.serviceStats}>
                                            <dt>Instances</dt>
                                            <dd
                                                className={
                                                    s.instancesOk != null &&
                                                    s.instancesTotal != null &&
                                                    s.instancesOk < s.instancesTotal
                                                        ? theme.bad
                                                        : undefined
                                                }>
                                                {s.instancesTotal == null ? '—' : `${s.instancesOk}/${s.instancesTotal}`}
                                            </dd>
                                            <dt>Req / min</dt>
                                            <dd>{orDash(s.requestsPerMinute, formatCount)}</dd>
                                            <dt>p95</dt>
                                            <dd>{orDash(s.p95Ms, formatMs)}</dd>
                                            <dt>5xx</dt>
                                            <dd className={(s.errorRate ?? 0) > 1 ? theme.bad : undefined}>
                                                {orDash(s.errorRate, formatPercent)}
                                            </dd>
                                            <dt>Updated</dt>
                                            <dd title={s.versionLabel ?? undefined}>
                                                {s.updatedAt ? timeAgo(s.updatedAt, now) : '—'}
                                            </dd>
                                        </dl>
                                        {s.causes.length > 0 && (
                                            <ul className={theme.causes}>
                                                {s.causes.map((c) => (
                                                    <li key={c}>{c}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </FlatCard>

                        <FlatCard
                            title={
                                <span className={theme.liveTitle}>
                                    <span className={`${theme.dot} ${theme.ok} ${theme.pulse}`} /> Events (24h)
                                </span>
                            }
                            className={theme.feedCard}
                            contentClassName={theme.feedContent}
                            loading={loading}>
                            <SectionError message={errorFor('events')} prefix="Events unavailable" />
                            <div className={theme.filters}>
                                {feedFilters.map((f) => (
                                    <button
                                        key={f.key}
                                        type="button"
                                        className={theme.chip}
                                        aria-pressed={feedFilter === f.key}
                                        onClick={() => setFeedFilter(f.key)}>
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                            {data && data.events.length === 0 && !errorFor('events') && (
                                <p className={theme.muted}>No events in the last 24 hours.</p>
                            )}
                            <ol className={theme.feed}>
                                {(data?.events ?? [])
                                    .filter((e) => feedFilter === 'all' || e.source === feedFilter)
                                    .map((e) => (
                                        <li key={e.id} className={`${theme.feedItem} ${theme[e.severity]}`}>
                                            <FontAwesomeIcon
                                                icon={sourceIcon[e.source]}
                                                className={theme.feedIcon}
                                                fixedWidth
                                            />
                                            <div className={theme.feedBody}>
                                                <div className={theme.feedMessage}>{e.message}</div>
                                                <div className={theme.muted}>
                                                    {duplicateNames.has(e.service) ? e.environmentName : e.service}
                                                </div>
                                            </div>
                                            <time className={theme.muted} dateTime={e.at.toISOString()}>
                                                {timeAgo(e.at, now)}
                                            </time>
                                        </li>
                                    ))}
                            </ol>
                        </FlatCard>
                    </div>

                    <div className={theme.bottom}>
                        <FlatCard title="Top Sentry issues (24h)" actions={<SourceBadge source={sentry} />}>
                            {sentry.error && (
                                <AlertBanner
                                    type="warning"
                                    className={theme.sourceError}
                                    title={
                                        sentry.data
                                            ? "Sentry's latest update failed; showing issues from an earlier one"
                                            : 'Could not load Sentry issues'
                                    }
                                    actions={[{ text: 'Try again', onClick: reloadSentry }]}>
                                    {sentry.error}
                                </AlertBanner>
                            )}
                            <Loaded source={sentry}>
                                {(sentryIssues) => (
                                    <table className={theme.table}>
                                        <thead>
                                            <tr>
                                                <th>Issue</th>
                                                <th>Project</th>
                                                <th className={theme.num}>Events</th>
                                                <th className={theme.num}>Users</th>
                                                <th className={theme.num}>Last seen</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sentryIssues.slice(0, 10).map((i) => (
                                                <tr key={i.shortId}>
                                                    <td>
                                                        <span className={`${theme.level} ${theme[i.level]}`}>{i.level}</span>
                                                        {i.permalink ? (
                                                            <a
                                                                className={theme.issueTitle}
                                                                href={i.permalink}
                                                                target="_blank"
                                                                rel="noreferrer">
                                                                {i.title}
                                                            </a>
                                                        ) : (
                                                            <span className={theme.issueTitle}>{i.title}</span>
                                                        )}
                                                        <div className={theme.muted}>{i.shortId}</div>
                                                    </td>
                                                    <td className={theme.muted}>{i.project}</td>
                                                    <td className={theme.num}>{i.count}</td>
                                                    <td className={theme.num}>{i.users}</td>
                                                    <td className={`${theme.num} ${theme.muted}`}>
                                                        {timeAgo(i.lastSeen, now)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </Loaded>
                        </FlatCard>

                        <FlatCard title={`CloudWatch alarms · ${stageLabel[stage]}`} loading={loading}>
                            <SectionError message={errorFor('alarms')} prefix="Alarms unavailable" />
                            {alarms?.length === 0 && <p className={theme.muted}>No alarms firing.</p>}
                            {!!alarms?.length && (
                                <ul className={theme.alarms}>
                                    {alarms.map((a) => (
                                        <li key={a.name}>
                                            <div className={theme.alarmName}>
                                                <FontAwesomeIcon icon={faBell} /> {a.name}
                                            </div>
                                            <div className={theme.muted}>{a.reason}</div>
                                            {a.since && (
                                                <div className={theme.muted}>
                                                    Firing for {timeAgo(a.since, now).replace(' ago', '')}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </FlatCard>
                    </div>
                </>
            )}
            <ServiceReport environmentName={report} now={now} onClose={() => setReport(null)} />
        </div>
    );
};

interface TldrItem {
    tone: Health;
    text: string;
}

/** The one-line "what's going on" summary for the ticker, worst news first. */
function buildTldr(
    latest: AwsHealth | null,
    awsError: string | null,
    issues: SentryIssue[] | null,
    now: number
): TldrItem[] {
    const items: TldrItem[] = [];
    if (awsError) {
        items.push({ tone: 'degraded', text: `AWS health unavailable: ${awsError}` });
    }
    for (const s of [...stages].reverse()) {
        for (const service of latest?.stages.find((x) => x.stage === s)?.unhealthy ?? []) {
            // Only the open stage has EB's explanation loaded.
            const cause =
                latest?.stage === s
                    ? latest.services.find((x) => x.environmentName === service.environmentName)?.causes[0]
                    : undefined;
            const status = service.healthStatus ? ` (${service.healthStatus})` : '';
            items.push({
                tone: service.health,
                text: `${stageLabel[s]}: ${service.name} is ${healthLabel[service.health].toLowerCase()}${status}${cause ? `: ${cause}` : ''}`,
            });
        }
    }
    const alarms = latest?.alarms ?? [];
    if (alarms.length) {
        items.push({
            tone: 'degraded',
            text: `${alarms.length} CloudWatch alarm${alarms.length > 1 ? 's' : ''} firing: ${alarms.map((a) => a.name).join(', ')}`,
        });
    }
    const top = issues?.[0];
    if (issues && top) {
        items.push({
            tone: top.level === 'warning' ? 'degraded' : 'down',
            text: `Top Sentry issue: ${top.title} (${top.count.toLocaleString('en-GB')} events, ${top.users} users, last seen ${timeAgo(top.lastSeen, now)}) in ${top.project}`,
        });
        const total = issues.reduce((sum, i) => sum + i.count, 0);
        items.push({
            tone: 'ok',
            text: `${issues.length} unresolved Sentry issues with ${total.toLocaleString('en-GB')} events in the last 24h`,
        });
    }
    const lastDeploy = latest?.events.find((e) => e.source === 'deploy');
    if (latest && lastDeploy) {
        items.push({
            tone: 'ok',
            text: `Last deploy in ${stageLabel[latest.stage]}: ${lastDeploy.service} ${timeAgo(lastDeploy.at, now)}`,
        });
    }
    if (!latest && !awsError) {
        items.unshift({ tone: 'unknown', text: 'Loading AWS health…' });
    } else if (latest && !items.some((i) => i.tone !== 'ok')) {
        items.unshift({ tone: 'ok', text: 'All systems operational' });
    }
    return items;
}

const Ticker: React.FC<{ items: TldrItem[]; tone: Health }> = ({ items, tone }) => {
    const row = (hidden: boolean) => (
        <div className={theme.tickerRow} aria-hidden={hidden || undefined}>
            {items.map((item, i) => (
                <span key={i} className={theme.tickerItem}>
                    <span className={`${theme.dot} ${theme[item.tone]}`} />
                    {item.text}
                </span>
            ))}
        </div>
    );
    // Two identical rows scrolled by -50% make a seamless loop.
    return (
        <div className={`${theme.ticker} ${theme[tone]}`} role="status" aria-label="Status summary">
            <span className={theme.tickerLabel}>TL;DR</span>
            <div className={theme.tickerViewport}>
                <div className={theme.tickerTrack}>
                    {row(false)}
                    {row(true)}
                </div>
            </div>
        </div>
    );
};

/** One AWS section failed on its own, usually a missing IAM permission; the rest of the page still works. */
const SectionError: React.FC<{ message?: string; prefix: string }> = ({ message, prefix }) =>
    message ? (
        <p className={theme.sectionError}>
            {prefix}: {message}
        </p>
    ) : null;

export default SystemHealthPage;
